"use client";

import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { registerUserSchema } from "@/lib/schemas/validationSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, Text } from "@radix-ui/themes";
import axios, { HttpStatusCode } from "axios";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import Link from "next/link";
import { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Criteria } from "./SignupPasswordInput";
import SignupPasswordInput from "./SignupPasswordInput";
import { Loader2 } from "lucide-react";
import PasswordInput from "@/app/components/form/PasswordInput";

interface CredentialsFormsProps {
  loggingIn: boolean;
  setLoggingIn: Dispatch<SetStateAction<boolean>>;
  callbackURL: string;
  router: AppRouterInstance;
  onComplete?: any;
}

type RegisterForm = z.infer<typeof registerUserSchema>;

const CredentialsSignUpForm = ({
  loggingIn,
  setLoggingIn,
  callbackURL,
  router,
  onComplete,
}: CredentialsFormsProps) => {
  const [error, setError] = useState("");
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({ resolver: zodResolver(registerUserSchema) });
  const [criteria, setCriteria] = useState<Criteria>({
    length: false,
    uppercase: false,
    number: false,
  });
  const unsetError = () => setError("");
  const handlePasswordChange = (e: any) => {
    const value = e.target.value;
    unsetError();

    setCriteria({
      length: value.length >= 8,
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
    });
  }

  // Call the register endpoint
  async function handleSignUp(formData: RegisterForm) {
    setLoggingIn(true);
    try {
      const res = await axios.post("/api/register", formData);

      if (res.status !== HttpStatusCode.Ok) {
        console.log(JSON.stringify(res, null, 2));
        throw new Error(res.data.error);
      } else {
        router.push(
          `/email/verify/send?email=${formData.email}&name=${formData.firstName}`
        );
        router.refresh();
        setLoggingIn(false);
        if (onComplete) onComplete();
      }
    } catch (e: any) {
      setLoggingIn(false);
      setError((_) =>
        e.response.data.error
          ? e.response.data.error
          : "An unexpected error has occurred."
      );
    }
  }

  return (
    <form className="w-full" onSubmit={handleSubmit(handleSignUp)}>
      <Flex direction="column" className="gap-2 max-h-84">
        <Flex direction="column" className="w-full gap-2 mb-2">
          <Flex className="gap-2">
            <Flex direction="column" className="w-full space-y-0">
              <Input
                disabled={loggingIn}
                placeholder="First Name"
                className="h-12 rounded-md"
                type="text"
                required={true}
                {...register("firstName", {
                  onChange: unsetError,
                })}
              />
              {errors.firstName && <p className="text-red-600">{errors.firstName.message}</p>}
            </Flex>
            <Flex direction="column" className="w-full space-y-0">
              <Input
                disabled={loggingIn}
                placeholder="Last Name"
                className="h-12 rounded-md"
                type="text"
                required={true}
                {...register("lastName", {
                  onChange: unsetError,
                })}
              />
              {errors.lastName && <p className="text-red-600">{errors.lastName.message}</p>}
            </Flex>
          </Flex>

          <Input
            disabled={loggingIn}
            placeholder="Email"
            className="h-12 rounded-md"
            type="email"
            required={true}
            {...register("email", {
              onChange: unsetError,
            })}
          />
          <SignupPasswordInput
            criteria={criteria}
            disabled={loggingIn}
            placeholder="Password"
            className="h-12 rounded-md"
            type="password"
            required={true}
            {...register("password", {
              onChange: handlePasswordChange,
            })}
          />
          <PasswordInput
            disabled={loggingIn}
            placeholder="Confirm Password"
            className="h-12 rounded-md"
            type="password"
            required={true}
            {...register("confirmPassword", {
              required: true,
              validate: (val: string) => {
                if (watch("password") !== val)
                  return "Your passwords do not match";
              },
              onChange: unsetError,
            })}
          // {...register("confirmPassword")}
          />
        </Flex>
        <Flex direction="column" align="center">
          {(errors.password ||
            errors.confirmPassword ||
            errors.email ||
            error) && (
              <p className="mb-2 text-red-600">
                {errors.password?.message ||
                  errors.confirmPassword?.message ||
                  errors.email?.message ||
                  error}
              </p>
            )}
          <Button
            disabled={loggingIn}
            type="submit"
            className={"color-inverting-button-base h-12 hover:bg-white w-full"}
          >
            {loggingIn &&
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            }
            Create Account
          </Button>
        </Flex>

        <Flex className="flex-grow" justify="center">
          <p className="text-sm w-3/5 text-center">
            By signing up, you agree to our{" "}
            <Link
              target="_blank"
              href="https://biddify-docs.s3.us-east-1.amazonaws.com/terms.pdf"
              className="underline-offset-2 underline text-zinc-700"
            >
              Terms of Use
            </Link>{" "}
            and{" "}
            <Link
              target="_blank"
              href="https://biddify-docs.s3.us-east-1.amazonaws.com/privacy.pdf"
              className="underline-offset-2 underline  text-zinc-700"
            >
              Privacy Policy
            </Link>.
          </p>
        </Flex>
      </Flex>
    </form>
  );
};

export default CredentialsSignUpForm;
